import { TimelineMapModel } from './timeline-map.model';
import { CameraModel } from '../../engine/models/camera.model';
import { LabelModel } from './labels/label.model';
import { Type, Exclude, Expose, plainToClass } from 'class-transformer';
import { SynchronizationModel } from './synchronization.model';
import { ChapterGraphModel } from './chapter-graph.model';
import { ChapterUIModel } from './chapter-ui.model';
import { HotspotModel } from './labels/hotspot.model';
import { BackgroundModel } from 'src/app/engine/models/background.model';
import { MediaModel } from './media.model';
import { TransitionModel } from './transition.model';
import { PostProcessingEffect } from 'src/app/engine/models/post-processing/effects';
import { TransparencyModeEnum } from 'src/app/engine/models/post-processing/transparency-mode.enum';
import { ShowObjects } from 'src/app/engine/models/show-objects.model';
import { ClippingPlanes } from './clipping-plane.model';
import { ChapterLegendModel } from './chapter-legend.model';
import { ChapterReadyStateModel } from './chapter-ready-state.model';
import { VisibilityLayersModel } from './visibility-layers.model';
import { ContentBlock } from 'src/app/content-blocks/models/content-block';
import { CameraActionModel } from './camera-action.model';
import { QuizQuestion } from 'src/app/quiz/models/quiz-question.model';

/* The Chapter model as it pertains to the application The engine is responsible
for building animation timelines and sychronizing the state of the scene-graph
at the start of each chapter. This data is stored in the Human Engine's chapter
object.

Engine responsibilities for faithfully setting chapter state include 1 - graph
synchronization. the state of each object in the scene-graph 2 - clipping
planes. These must be in effect simultaneously with the graph synchronization,
or else flashing may occur. 3 - camera position and action. 4 - lights.

Engine responsibilities for building chapter 1- building of the animations and
the corresponding timeline maps to map chapter time to animation time

However, the engine's chapter object stores several data attributes that it need
not be responsible for managing. Examples include: 1 - background color 2 - meta
data, display name and description 3 - labels (however, they do contain
engine-specific data for tracking points in space via markers) 4 - customData -
special, protected location scoped on the chapter for custom or in-development
features.

NOTE: EXCLUDE/EXPOSE
The human chapter object contains many properties that we do not want exposed to the application.
We use @Exclude/@Expose decorators to insist that only the @Expose'd properties are passed to the application state.

*/

declare const immer: {
    readonly immerable: unique symbol;
};

@Exclude()
export class ChapterModel {
    [immer.immerable] = true;

    // Define nested types --------------------------------------------------
    @Type(() => LabelModel)
    @Type(() => SynchronizationModel)
    @Type(() => CameraModel)
    @Type(() => ChapterGraphModel)

    // Shared Data between Human Chapter and App Chapter ---------------------
    @Expose()
    id: string;

    @Expose()
    info: {
        displayName: string,
        description: string,
    };

    @Expose()
    labels: LabelModel[];

    @Expose()
    colorRecipeObjects: any;

    @Expose()
    hotspots?: HotspotModel[];

    @Expose()
    synchronization: SynchronizationModel;

    @Expose()
    background: BackgroundModel;
    
    @Expose()
    lights: {
        libraries: string[],
        lights: string[]
    };

    @Expose()
    timelineMap: TimelineMapModel[];

    @Expose()
    camera: CameraActionModel;

    @Expose()
    transitions?: {
        post?: TransitionModel;
        pre?: TransitionModel;
    };

    @Expose()
    properties: {
        [key: string]: string;
    };

    @Expose()
    loop: boolean;

    @Expose()
    customData: {
        [key: string]: any;
        postprocess: PostProcessingEffect[];
        particleSystems?: string[]
    };

    @Expose()
    clippingPlanes: ClippingPlanes;

    @Expose()
    ui: ChapterUIModel;

    @Expose()
    graph: ChapterGraphModel;

    @Expose()
    viewModes: any;

    @Expose()
    recipes: any;

    @Expose()
    transparencyMode: TransparencyModeEnum;

    @Expose()
    fallbackTransparency: any;

    @Expose()
    effects: any;

    @Expose()
    environment?: {
        lightMap?: string[],
        reflectionMap?: string[]
    };
    
    @Expose()
    pickThroughObjects: Record<string, boolean>;
    
    @Expose()
    showObjects: ShowObjects;

    @Expose()
    options: any;

    @Expose()
    media: {
        [key: string]: MediaModel;
    };

    @Expose()
    legend: ChapterLegendModel[];

    @Expose()
    visibilityLayers: VisibilityLayersModel;

    @Expose()
    contentBlocks: ContentBlock[];

    @Expose()
    quizQuestions: QuizQuestion[];

    // new data not in human chapter
    readyState: ChapterReadyStateModel;
}
